import config from "config/config";
import { jwt } from "api/jwt";
import type { BoomHelpMessage } from "api/boom-help/types";

type StreamedMessage = Pick<BoomHelpMessage, "id" | "status" | "content"> &
  Partial<BoomHelpMessage>;

const FRAME_SEPARATOR = "\n\n";
const FINAL_STATUSES = new Set(["completed", "failed"]);

const parseFrame = (frame: string): StreamedMessage | null => {
  const data = frame
    .split("\n")
    .filter((line) => line.startsWith("data:"))
    .map((line) => line.slice(5).trim())
    .join("");
  return data ? (JSON.parse(data) as StreamedMessage) : null;
};

const openStream = async (url: string, signal: AbortSignal) => {
  await jwt.ensureAccessToken();
  const request = () =>
    fetch(url, {
      signal,
      headers: {
        Accept: "text/event-stream",
        Authorization: `Bearer ${jwt.getAccessToken()}`
      }
    });
  const response = await request();
  if (response.status !== 401) return response;
  await jwt.refresh();
  return request();
};

export const streamBoomHelpReply = async ({
  conversationId,
  messageId,
  signal,
  onMessage
}: {
  conversationId: number;
  messageId: number;
  signal: AbortSignal;
  onMessage: (message: StreamedMessage) => void;
}) => {
  const response = await openStream(
    `${config.apiHost}/portal/v1/boom_help/conversations/${conversationId}/messages/${messageId}/stream`,
    signal
  );
  if (!response.ok || !response.body)
    throw new Error(`Boom Help stream failed (${response.status})`);

  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let finished = false;

  const drain = (buffer: string): string => {
    const end = buffer.indexOf(FRAME_SEPARATOR);
    if (end === -1) return buffer;
    const message = parseFrame(buffer.slice(0, end));
    if (message) {
      finished = FINAL_STATUSES.has(message.status);
      onMessage(message);
    }
    return drain(buffer.slice(end + FRAME_SEPARATOR.length));
  };

  const read = async (buffer: string): Promise<void> => {
    const { value, done } = await reader.read();
    if (done) return;
    return read(drain(buffer + value));
  };

  await read("");
  if (!finished)
    throw new Error("Boom Help stream ended before the reply finished");
};
